import type { ProjectType, ValidationDecision, ValidationRecommendation } from '../../core/types/data-models';
import type { SemanticColor } from '../../shared/components/semanticColor';
import { decisionDisplayFor, recommendationDisplayFor } from './recommendationDisplay';

/**
 * Mirrors SOURCE_BRIEF_HEADING in
 * backend/src/modules/projects/projects/project-prompt.util.ts: the first
 * line of a project prompt once its public sources were analyzed into a
 * company brief. Its presence is what makes the first step "done" for a
 * business-validation project.
 */
export const SOURCE_BRIEF_HEADING = '# Company brief';

export function hasSourceBrief(prompt: string | null | undefined): boolean {
  return (prompt ?? '').trimStart().startsWith(SOURCE_BRIEF_HEADING);
}

export interface BlockCopy {
  title: string;
  subtitle: string;
}

/**
 * The wording of the validation page and its summary, per project type: a
 * founder validates an idea and decides go / no-go, an investor validates a
 * company and decides invest / pass, or a stock and decides buy / hold /
 * sell. The components are shared; only this changes.
 */
export interface ValidationCopy {
  title: string;
  intro: {
    stepLabel: string;
    title: string;
    subtitle: string;
    empty: string;
    /** Shown over the progress while the sources are analyzed. */
    analyzing: string;
    /** The buttons that (re)run the source analysis; null for a type without one. */
    analyzeFirstAction: string | null;
    analyzeAgainAction: string | null;
    analyzeHint: string;
  };
  start: {
    title: string;
    body: (firstStage: string | null) => string;
    button: string;
    /** The start panel of a business project whose sources were never analyzed. */
    analyzeTitle: string;
    analyzeBody: string;
    analyzeButton: string;
  };
  regenerateMessage: (stage: string) => string;
  summary: {
    buildBody: string;
    conditionsTitle: string;
    statementTitle: string;
    scorecardSubtitle: string;
    strengths: BlockCopy;
    risks: BlockCopy;
    market: BlockCopy;
    customer: BlockCopy;
    competition: BlockCopy;
    gtm: BlockCopy & { triggerLabel: string };
    experiments: BlockCopy;
  };
  recommendation: Record<ValidationRecommendation, { label: string; color: SemanticColor }>;
  decision: Record<ValidationDecision, { label: string; color: SemanticColor }>;
}

const IDEA_COPY: ValidationCopy = {
  title: 'Idea validation',
  intro: {
    stepLabel: 'Describe your idea',
    title: 'Your idea',
    subtitle: 'What you described when creating the project. Every stage is generated from it.',
    empty: 'No idea description was recorded for this project.',
    analyzing: '',
    analyzeFirstAction: null,
    analyzeAgainAction: null,
    analyzeHint: '',
  },
  start: {
    title: 'Start the validation',
    body: (firstStage) =>
      `Each stage generates one document that answers its questions about your idea${firstStage ? `, starting with ${firstStage}` : ''}.`,
    button: 'Start validation',
    analyzeTitle: '',
    analyzeBody: '',
    analyzeButton: '',
  },
  regenerateMessage: (stage) =>
    `${stage} will be generated again from your idea. Its current content will be replaced, including any edits you made.`,
  summary: {
    buildBody:
      'Every stage is generated. The summary weighs all of them into one recommendation, with the scorecard, the strengths and risks, the key numbers and the assumptions to test before you commit.',
    conditionsTitle: 'To turn this into a go',
    statementTitle: 'The value in one sentence',
    scorecardSubtitle:
      "The average verdict score of each stage's questions, from 1 (negative for the idea) to 10 (positive). Expand a stage for its answers.",
    strengths: { title: 'What supports a go', subtitle: 'The strongest evidence for the idea, with the stage that established it.' },
    risks: { title: 'What could kill it', subtitle: 'The findings most likely to stop the idea, with the stage that raised them.' },
    market: { title: 'Market & demand', subtitle: 'How big the opportunity is, how fast it grows, and what proves people already pay.' },
    customer: { title: 'Target customer', subtitle: 'Who to sell to first, who uses it, who pays, and who is likely to switch.' },
    competition: {
      title: 'Competition',
      subtitle: 'What the target customers use today, who competes for them, and what none of them solve.',
    },
    gtm: {
      title: 'Path to the first customers',
      subtitle: 'How the first paying customers are won, and how long it takes.',
      triggerLabel: 'Trial trigger',
    },
    experiments: {
      title: 'What to test before committing',
      subtitle: 'The riskiest assumptions, in order, with a cheap test and the result that counts as a pass.',
    },
  },
  recommendation: recommendationDisplayFor('IDEA_VALIDATION'),
  decision: decisionDisplayFor('IDEA_VALIDATION'),
};

const BUSINESS_COPY: ValidationCopy = {
  title: 'Business validation',
  intro: {
    stepLabel: 'Company sources',
    title: 'Company brief',
    subtitle: "The brief written from the company's public sources, and your focus points. Every stage is generated from it.",
    empty: 'The company sources have not been analyzed yet. Only your notes and the links are recorded.',
    analyzing: 'Reading the company sources and writing the brief…',
    analyzeFirstAction: 'Analyze sources',
    analyzeAgainAction: 'Analyze sources again',
    analyzeHint: 'Reads the current links again and rewrites the brief. Stages already generated keep their content.',
  },
  start: {
    title: 'Start the validation',
    body: (firstStage) =>
      `Each stage generates one due-diligence document about the company${firstStage ? `, starting with ${firstStage}` : ''}.`,
    button: 'Start validation',
    analyzeTitle: 'Analyze the company sources',
    analyzeBody:
      'Vibelin reads the website and the other links you gave, writes the company brief, and then generates the first stage from it.',
    analyzeButton: 'Analyze sources',
  },
  regenerateMessage: (stage) =>
    `${stage} will be generated again from the company brief. Its current content will be replaced, including any edits you made.`,
  summary: {
    buildBody:
      'Every stage is generated. The summary weighs all of them into one recommendation, with the scorecard, the strengths and risks, the key numbers, the traction and deal picture, and the checks to run before you commit.',
    conditionsTitle: 'To make this an invest',
    statementTitle: 'The thesis in one sentence',
    scorecardSubtitle:
      "The average verdict score of each stage's questions, from 1 (negative for the investment case) to 10 (positive). Expand a stage for its answers.",
    strengths: {
      title: 'What supports investing',
      subtitle: 'The strongest evidence for the investment case, with the stage that established it.',
    },
    risks: {
      title: 'What could kill the deal',
      subtitle: 'The findings most likely to stop the investment, with the stage that raised them.',
    },
    market: { title: 'Market & headroom', subtitle: 'How big the market is, how fast it grows, and how much room the company has left.' },
    customer: { title: 'Customer base', subtitle: 'Who actually buys today, who uses it, who pays, and who is likely to switch.' },
    competition: {
      title: 'Competitive position',
      subtitle: 'Who the company competes with, how customers compare them, and the gaps none of them close.',
    },
    gtm: {
      title: 'Growth engine',
      subtitle: 'How the company wins customers today, how efficient that is, and where the next growth comes from.',
      triggerLabel: 'Next growth',
    },
    experiments: {
      title: 'What to check before committing',
      subtitle: 'The thesis assumptions in order, with the diligence check that settles each and the result that counts as a pass.',
    },
  },
  recommendation: recommendationDisplayFor('BUSINESS_VALIDATION'),
  decision: decisionDisplayFor('BUSINESS_VALIDATION'),
};

const STOCK_COPY: ValidationCopy = {
  title: 'Stock validation',
  intro: {
    stepLabel: 'Pick the stock',
    title: 'The stock',
    subtitle: 'The exchange and the symbol you entered when creating the project. Every stage is generated from them.',
    empty: 'No stock was recorded for this project.',
    analyzing: '',
    analyzeFirstAction: null,
    analyzeAgainAction: null,
    analyzeHint: '',
  },
  start: {
    title: 'Start the validation',
    body: (firstStage) =>
      `Each stage generates one research document about the stock${firstStage ? `, starting with ${firstStage}` : ''}.`,
    button: 'Start validation',
    analyzeTitle: '',
    analyzeBody: '',
    analyzeButton: '',
  },
  regenerateMessage: (stage) =>
    `${stage} will be generated again for this stock. Its current content will be replaced, including any edits you made.`,
  summary: {
    buildBody:
      'Every stage is generated. The summary weighs all of them into one buy, hold or sell call, with the price frame, the scorecard, the strengths and risks, the key numbers and the signals to watch.',
    conditionsTitle: 'To make this a buy',
    statementTitle: 'The thesis in one sentence',
    scorecardSubtitle:
      "The average verdict score of each stage's questions, from 1 (negative for owning the stock) to 10 (positive). Expand a stage for its answers.",
    strengths: {
      title: 'What supports buying',
      subtitle: 'The strongest evidence for owning the shares, with the stage that established it.',
    },
    risks: {
      title: 'What could sink the stock',
      subtitle: 'The findings most likely to hurt the share price, with the stage that raised them.',
    },
    market: { title: 'Industry', subtitle: 'How big the industry is, how fast it grows, and the demand signals behind it.' },
    customer: { title: 'Customers', subtitle: 'Who buys from the company.' },
    competition: {
      title: 'Competitive position',
      subtitle: 'Who the company competes with, their size and threat, and the gaps the company could win.',
    },
    gtm: { title: 'Growth engine', subtitle: 'Where the growth comes from.', triggerLabel: 'Next growth' },
    experiments: {
      title: 'What to watch before acting',
      subtitle: 'The thesis assumptions in order, with the check or signal that settles each and the result that counts as a pass.',
    },
  },
  recommendation: recommendationDisplayFor('STOCK_VALIDATION'),
  decision: decisionDisplayFor('STOCK_VALIDATION'),
};

/** A project type without a guided validation reads like an idea; its page shows no stages anyway. */
export function validationCopyFor(projectType: ProjectType): ValidationCopy {
  if (projectType === 'BUSINESS_VALIDATION') return BUSINESS_COPY;
  if (projectType === 'STOCK_VALIDATION') return STOCK_COPY;
  return IDEA_COPY;
}
